import React, { useEffect, useState, Children, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, FileIcon, UserIcon, PenIcon, SendIcon, CheckCircleIcon, UploadIcon, MessageSquareIcon, ShieldCheckIcon, CloudIcon, FileText, DatabaseIcon, FilePenIcon } from 'lucide-react';
import { isMobile } from '../utils/deviceDetection';
export const Hero = () => {
  const checklistItems = ['Send document requests', 'Collect files securely', 'Automate reminders', 'Extract data with AI', 'Sync with your tools'];
  // Company logos with real images
  const companyLogos = [{
    name: 'RelisLaw',
    logo: 'https://i.imgur.com/NjbdOnv.png'
  }, {
    name: 'Vrapi Weeks',
    logo: 'https://i.imgur.com/wjQKPxl.png'
  }, {
    name: 'Coaction',
    logo: 'https://i.imgur.com/u9XTdqO.png'
  }, {
    name: 'EFG Companies',
    logo: 'https://i.imgur.com/5xN9Dow.png'
  }, {
    name: 'Invisor Consulting Services',
    logo: 'https://i.imgur.com/fVlhtaE.png'
  }, {
    name: 'SBLI',
    logo: 'https://i.imgur.com/aZPU0k2.png'
  }, {
    name: 'FH',
    logo: 'https://i.imgur.com/ZU1QqTa.png'
  }, {
    name: 'Moore Grider',
    logo: 'https://i.imgur.com/wq6aWsz.png'
  }, {
    name: 'Dots',
    logo: 'https://i.imgur.com/rHiMvDH.png'
  }, {
    name: 'Sapochnick Law Firm',
    logo: 'https://i.imgur.com/TQqYjlu.png'
  }, {
    name: 'Ten Four Truck Insurance',
    logo: 'https://i.imgur.com/biLNN12.png'
  }, {
    name: 'Jacobs & Schlesinger',
    logo: 'https://i.imgur.com/Dz5sOEO.png'
  }, {
    name: 'Gridiron',
    logo: 'https://i.imgur.com/NDSrBQH.png'
  }, {
    name: 'Goldstar Mortgage (GA)',
    logo: 'https://i.imgur.com/XTgIo4x.png'
  }, {
    name: 'Real Savvy Financial Advice',
    logo: 'https://i.imgur.com/1er7iWj.png'
  }, {
    name: 'Delagnes Mitchell & Linder',
    logo: 'https://i.imgur.com/8poCkja.png'
  }, {
    name: 'Jarrett & Price',
    logo: 'https://i.imgur.com/UhwLnPP.png'
  }, {
    name: 'Casaduana',
    logo: 'https://i.imgur.com/JYuNPFt.png'
  }, {
    name: 'Pachuta & Kammerman, PLLC',
    logo: 'https://i.imgur.com/BCp62sX.png'
  }];
  // Create a duplicate array for seamless looping
  const duplicatedLogos = [...companyLogos, ...companyLogos];
  // Simplified animation variants for mobile
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile() ? 0.05 : 0.15,
        delayChildren: isMobile() ? 0.1 : 0.3 // Less delay on mobile
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: isMobile() ? 10 : 20 // Smaller distance on mobile
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: isMobile() ? 'tween' : 'spring',
        stiffness: isMobile() ? 50 : 100,
        duration: isMobile() ? 0.3 : undefined // Explicit duration for mobile
      }
    }
  };
  return <>
      <section className="min-h-[70vh] flex flex-col justify-center pt-24 pb-8 px-4 sm:px-6 lg:px-8 relative overflow-x-hidden bg-[#f8faff]" data-id="element-329">
        {/* Dynamic background elements - Updated for better mobile display */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-330">
          <motion.div className="absolute top-0 right-0 w-[60%] max-w-[500px] h-[60%] rounded-full bg-gradient-to-b from-[#2E6BFF]/10 to-transparent blur-[100px]" style={{
          position: 'absolute',
          top: 0,
          right: 0,
          transform: 'translate(0, 0)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          willChange: isMobile() ? 'opacity' : 'transform, opacity'
        }} animate={isMobile() ? {
          opacity: [0.2, 0.3, 0.2]
        } // Simpler animation for mobile
        : {
          scale: [1, 1.05, 1],
          opacity: [0.3, 0.4, 0.3]
        } // Full animation for desktop
        } transition={{
          duration: isMobile() ? 5 : 8,
          repeat: Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut'
        }} data-id="element-331" />
          <motion.div className="absolute bottom-0 left-0 w-[50%] max-w-[400px] h-[50%] rounded-full bg-gradient-to-t from-[#0BB489]/10 to-transparent blur-[80px]" style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          transform: 'translate(0, 0)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          willChange: isMobile() ? 'opacity' : 'transform, opacity'
        }} animate={isMobile() ? {
          opacity: [0.1, 0.2, 0.1]
        } // Simpler animation for mobile
        : {
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.3, 0.2]
        } // Full animation for desktop
        } transition={{
          duration: isMobile() ? 6 : 10,
          repeat: Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut',
          delay: 1
        }} data-id="element-332" />
        </div>
        <div className="max-w-7xl mx-auto w-full flex flex-col flex-grow justify-center my-auto" data-id="element-333">
          <div className="grid md:grid-cols-2 gap-8 items-center relative z-10" data-id="element-334">
            <motion.div initial={{
            opacity: 0,
            y: isMobile() ? 10 : 20 // Smaller distance on mobile
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: isMobile() ? 0.5 : 0.7 // Faster animation on mobile
          }} className="text-center md:text-left flex flex-col" data-id="element-335">
              <motion.h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-full" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.7,
              delay: 0.2
            }} data-id="element-336">
                <span className="bg-gradient-to-r from-[#0B1220] to-[#2E6BFF] bg-clip-text text-transparent" data-id="element-337">
                  Automated Document
                </span>
                <br data-id="element-338" />
                <span className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] bg-clip-text text-transparent" data-id="element-339">
                  Collection
                </span>
              </motion.h1>
              <motion.p className="mt-5 text-lg sm:text-xl text-[#5A6376] max-w-xl mx-auto md:mx-0" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.7,
              delay: 0.4
            }} data-id="element-340">
                Stop chasing clients for documents. Pigeon automates the process
                from end-to-end and syncs directly with your existing platforms.
              </motion.p>
              <motion.div className="mt-5" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.7,
              delay: 0.8
            }} data-id="element-341">
                <motion.div className="mb-4 inline-flex items-center px-5 py-2 rounded-full bg-[#F0F5FF] text-[#2E6BFF] border border-[#2E6BFF]/10 backdrop-blur-sm shadow-sm" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.5,
                delay: 0.6
              }} data-id="element-342">
                  <span className="text-sm font-medium" data-id="element-343">
                    No client login required — one-click, secure upload
                  </span>
                </motion.div>
                <div className="flex flex-col items-center md:items-start" data-id="element-344">
                  <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3.5 text-base font-medium rounded-full text-white relative overflow-hidden shadow-[0_8px_16px_rgba(46,107,255,0.25)] w-fit" whileHover={{
                  y: -2,
                  boxShadow: '0 12px 20px rgba(46,107,255,0.3)'
                }} whileTap={{
                  scale: 0.98
                }} style={{
                  background: 'linear-gradient(to right, #2E6BFF, #36D1DC)'
                }} data-id="element-345">
                    <span className="relative z-10" data-id="element-346">Book a Call</span>
                    <ArrowRightIcon className="ml-2 h-4 w-4 relative z-10" data-id="element-347" />
                    <div className="absolute inset-0 transition-all duration-300 opacity-0 hover:opacity-100" style={{
                    background: 'linear-gradient(to right, #2059E2, #32C3CE)'
                  }} data-id="element-348" />
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} className="relative" data-id="element-349">
              {/* Image Placeholder */}
              <motion.div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-white/50 w-full" style={{
              height: 'auto',
              maxHeight: {
                lg: '450px'
              }
            }} whileHover={{
              boxShadow: '0 20px 40px rgba(9,16,29,0.12)',
              y: -5
            }} transition={{
              duration: 0.5
            }} data-id="element-350">
                <div className="w-full h-full flex flex-col p-0" data-id="element-351">
                  {/* Email content */}
                  <div className="w-full flex-grow flex flex-col p-4 sm:p-5 bg-white rounded-lg" data-id="element-352">
                    {/* Email body with existing checklist */}
                    <div className="w-full h-full flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 sm:gap-8" data-id="element-353">
                      {/* Left Column - Logo and Text */}
                      <div className="flex flex-col items-center lg:items-start justify-center w-full lg:w-auto mb-6 lg:mb-0" data-id="element-354">
                        <div className="mb-1 sm:mb-2 flex items-center justify-center lg:justify-start" data-id="element-355">
                          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/f7vTc1xq41Q14pjQPwNLiy/ACME_Logo.png" alt="ACME Logo" className="h-6 sm:h-10 w-auto object-contain" data-id="element-356" />
                        </div>
                        <div className="text-xl sm:text-2xl font-semibold text-center lg:text-left" data-id="element-357">
                          <p data-id="element-358">Hi Ava,</p>
                          <p className="text-[#0B1220] mt-1" data-id="element-359">
                            Please submit these items for
                          </p>
                          <p className="text-[#2E6BFF]" data-id="element-360">ACME Corp.</p>
                        </div>
                      </div>
                      {/* Right Column - Checklist */}
                      <div className="w-full lg:max-w-md self-center" data-id="element-361">
                        <div className="grid gap-3 sm:gap-4 w-full" data-id="element-362">
                          {/* Proof of Insurance - Checked */}
                          <div className="flex items-center p-3 sm:p-4 bg-[#2E6BFF] text-white rounded-lg transition-all duration-200 hover:shadow-md" data-id="element-363">
                            <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center mr-4 flex-shrink-0" data-id="element-364">
                              <svg width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-365">
                                <path d="M10 3L4.5 8.5L2 6" stroke="#2E6BFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-366" />
                              </svg>
                            </div>
                            <div className="flex flex-col justify-center min-w-0" data-id="element-367">
                              <span className="text-sm font-medium truncate" data-id="element-368">
                                Proof of Insurance
                              </span>
                              <span className="text-xs text-gray-300 mt-1 flex items-center" data-id="element-369">
                                <UploadIcon className="h-3 w-3 mr-1" data-id="element-370" />
                                File Upload
                              </span>
                            </div>
                          </div>
                          {/* Service Agreement - Unchecked */}
                          <div className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50" data-id="element-371">
                            <div className="w-7 h-7 rounded-full border-2 border-gray-300 mr-4 flex-shrink-0" data-id="element-372"></div>
                            <div className="flex flex-col justify-center min-w-0" data-id="element-373">
                              <span className="text-sm font-medium text-gray-800 truncate" data-id="element-374">
                                Service Agreement
                              </span>
                              <span className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-375">
                                <PenIcon className="h-3 w-3 mr-1" data-id="element-376" />
                                E-Signature / Form
                              </span>
                            </div>
                          </div>
                          {/* Intake Form - Unchecked */}
                          <div className="flex items-center p-3 sm:p-4 border border-gray-200 rounded-lg transition-all duration-200 hover:border-gray-300 hover:bg-gray-50" data-id="element-377">
                            <div className="w-7 h-7 rounded-full border-2 border-gray-300 mr-4 flex-shrink-0" data-id="element-378"></div>
                            <div className="flex flex-col justify-center min-w-0" data-id="element-379">
                              <span className="text-sm font-medium text-gray-800 truncate" data-id="element-380">
                                SSN
                              </span>
                              <span className="text-xs text-gray-500 mt-1 flex items-center" data-id="element-381">
                                <MessageSquareIcon className="h-3 w-3 mr-1" data-id="element-382" />
                                Question
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Email footer */}
                  </div>
                </div>
                {/* Abstract decorative elements */}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Backed by Y Combinator */}
      <div className="flex items-center justify-center gap-2.5 pt-0 pb-8 bg-[#f8faff]" data-id="element-383">
        <span className="text-base text-[#5A6376] font-medium" data-id="element-384">Backed by</span>
        <img src="https://cdn.magicpatterns.com/uploads/hkH7QvRDsp7w9M9nY17rKF/YC_Logo_Expanded_—_Orange.svg" alt="Y Combinator" className="h-7 w-auto object-contain" data-id="element-385" />
      </div>
      {/* Logo ticker section */}
      <section className="py-2 w-full overflow-hidden" data-id="element-386">
        {/* COMMENTED OUT: Featured logos section - uncomment to restore
         <div className="max-w-4xl mx-auto px-6 mb-6 pt-2">
          <div className="flex flex-wrap sm:flex-nowrap items-center justify-center sm:justify-evenly gap-y-8 gap-x-6 sm:gap-x-0">
            {[
              {
                src: 'https://i.imgur.com/Jc7z8B3.png',
                alt: 'Featured logo 1',
                width: 'w-[180px] sm:w-[230px]',
              },
              {
                src: 'https://cdn.magicpatterns.com/uploads/8r2iKADj3NbuXu4dJ2SLnc/YTmXmIx.png',
                alt: 'Wholesure',
                width: 'w-[155px] sm:w-[195px]',
              },
              {
                src: 'https://i.imgur.com/u9XTdqO.png',
                alt: 'Featured logo 3',
                width: 'w-[135px] sm:w-[170px]',
              },
              {
                src: 'https://i.imgur.com/NDSrBQH.png',
                alt: 'Featured logo 4',
                width: 'w-[135px] sm:w-[170px]',
              },
            ].map((logo, index) => (
              <div
                key={index}
                className="flex items-center justify-center h-[60px] sm:h-[80px]"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className={`${logo.width} max-h-full object-contain hover:scale-105 transition-all duration-300`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
         </div>
         END COMMENTED OUT */}
        <motion.div className="w-full" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: isMobile() ? 0.4 : 0.7 // Faster fade-in on mobile
      }} data-id="element-387">
          <div className="relative" data-id="element-388">
            {/* Seamless logo ticker */}
            <div className="relative overflow-hidden w-full h-[70px] flex items-center" data-id="element-389">
              <motion.div className="flex absolute whitespace-nowrap" animate={{
              x: [`0%`, `-50%`]
            }} transition={{
              x: {
                duration: isMobile() ? 90 : 60,
                repeat: Infinity,
                ease: 'linear',
                repeatType: 'loop'
              }
            }} style={{
              width: `${companyLogos.length * 240 * 2}px` // Double width for the duplicate set
            }} data-id="element-390">
                {/* First set of logos */}
                <div className="flex gap-4 whitespace-nowrap" data-id="element-391">
                  {companyLogos.map((company, index) => <motion.div key={`logo-first-${index}`} className="flex-shrink-0 w-[240px] h-[80px] flex items-center justify-center p-2" whileHover={isMobile() ? {} : {
                  scale: 1.05,
                  filter: 'brightness(1.1)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-392">
                      <img src={company.logo} alt={`${company.name} logo`} className="w-auto h-auto max-w-[98%] max-h-[98%] object-contain" loading={isMobile() ? 'lazy' : 'eager'} // Lazy load images on mobile
                  data-id="element-393" />
                    </motion.div>)}
                </div>
                {/* Second set of logos for seamless looping */}
                <div className="flex gap-4 whitespace-nowrap" data-id="element-394">
                  {companyLogos.map((company, index) => <motion.div key={`logo-second-${index}`} className="flex-shrink-0 w-[240px] h-[80px] flex items-center justify-center p-2" whileHover={isMobile() ? {} : {
                  scale: 1.05,
                  filter: 'brightness(1.1)'
                }} transition={{
                  duration: 0.3
                }} data-id="element-395">
                      <img src={company.logo} alt={`${company.name} logo`} className="w-auto h-auto max-w-[98%] max-h-[98%] object-contain" loading="lazy" data-id="element-396" />
                    </motion.div>)}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>
    </>;
};